import { Box, IconButton, Stack, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import { useEffect, useMemo, useRef, useState } from "react";

const PRIMARY = "#3A54AF";
const SECONDARY = "#f44336";

function formatTime(seconds) {
  if (!seconds || Number.isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

export default function MessageBubble({ message }) {
  const isUser = message.role === "user";
  const isError = !!message.error;
  const isPending = !!message.pending;
  const hasAudio = !!message.audio;

  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!hasAudio) return;

    const audio = new Audio(message.audio);
    audioRef.current = audio;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
    };

    const handleEnded = () => {
      setPlaying(false);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.currentTime = 0;
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audioRef.current = null;
    };
  }, [message.audio, hasAudio]);

  const handleToggleAudio = () => {
    if (!audioRef.current) return;

    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setPlaying(true);
      }).catch(() => {});
    }
  };

  const bubbleStyles = useMemo(() => {
    if (isError) {
      return {
        background: "linear-gradient(135deg, #f44336 0%, #e53935 100%)",
        color: "#fff",
        border: "1px solid rgba(244,67,54,0.2)",
      };
    }

    if (isUser) {
      return {
        background: "linear-gradient(135deg, #3A54AF 0%, #506DCC 100%)",
        color: "#fff",
        border: "1px solid rgba(58,84,175,0.18)",
      };
    }

    return {
      background: "#ffffff",
      color: "#111827",
      border: "1px solid #e7ebf6",
    };
  }, [isError, isUser]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1.2}
        alignItems="flex-end"
        sx={{
          maxWidth: {
            xs: "92%",
            sm: "82%",
            md: "72%",
          },
          flexDirection: isUser ? "row-reverse" : "row",
        }}
      >
        {/* Avatar */}
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isUser ? "rgba(58,84,175,0.12)" : "rgba(17,24,39,0.06)",
            color: isUser ? PRIMARY : "#374151",
            flexShrink: 0,
          }}
        >
          {isUser ? (
            <PersonRoundedIcon sx={{ fontSize: 18 }} />
          ) : (
            <img src="/logo.png" width={18} height={18} alt="Assistant" />
          )}
        </Box>

        {/* Bubble */}
        <Box
          sx={{
            ...bubbleStyles,
            borderRadius: 4,
            px: 1.5,
            py: 1.3,
            boxShadow: isUser
              ? "0 10px 24px rgba(58,84,175,0.18)"
              : "0 8px 22px rgba(15,23,42,0.06)",
            backdropFilter: "blur(8px)",
          }}
        >
          {/* Audio row */}
          {hasAudio && (
            <Box
              sx={{
                mb: message.text ? 1.1 : 0,
                px: 0.5,
                py: 0.6,
                borderRadius: 3,
                backgroundColor: isUser
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(58,84,175,0.06)",
                border: isUser
                  ? "1px solid rgba(255,255,255,0.12)"
                  : "1px solid rgba(58,84,175,0.10)",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <IconButton
                  onClick={handleToggleAudio}
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: isUser ? "#ffffff" : PRIMARY,
                    color: isUser ? PRIMARY : "#ffffff",
                    "&:hover": {
                      bgcolor: isUser ? "#f8fafc" : "#30489a",
                    },
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  }}
                >
                  {playing ? (
                    <PauseRoundedIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />
                  )}
                </IconButton>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{ mb: 0.2 }}
                  >
                    <GraphicEqRoundedIcon
                      sx={{
                        fontSize: 18,
                        color: isUser ? "rgba(255,255,255,0.9)" : PRIMARY,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: isUser ? "#fff" : "#111827",
                      }}
                    >
                      Voice message
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    {/* <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 999,
                        overflow: "hidden",
                        backgroundColor: isUser
                          ? "rgba(255,255,255,0.20)"
                          : "rgba(58,84,175,0.12)",
                      }}
                    >
                      <Box
                        sx={{
                          width: playing ? "65%" : "28%",
                          height: "100%",
                          borderRadius: 999,
                          transition: "all 0.35s ease",
                          backgroundColor: isUser ? "#fff" : PRIMARY,
                        }}
                      />
                    </Box> */}

                    <Typography
                      sx={{
                        fontSize: 12,
                        minWidth: 36,
                        textAlign: "right",
                        color: isUser
                          ? "rgba(255,255,255,0.9)"
                          : "#6b7280",
                      }}
                    >
                      {formatTime(duration)}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Text */}
          {!!message.text && (
            <Stack direction="row" spacing={0.8} alignItems="flex-start">
              {isError && (
                <ErrorOutlineRoundedIcon
                  sx={{
                    fontSize: 18,
                    mt: "2px",
                    flexShrink: 0,
                    color: "#fff",
                  }}
                />
              )}

              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  fontWeight: 500,
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                  opacity: isPending ? 0.85 : 1,
                }}
              >
                {message.text}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}